/**
 * AppointmentDetailsPage
 * Page for viewing appointment details — redesigned with card layout
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  useTheme,
} from '@mui/material';
import {
  Edit,
  Cancel,
  CheckCircle,
  Done,
  ArrowBack,
  Science,
  Send,
  Person,
  LocationOn,
  Description,
  AccessTime,
  EventBusy,
  Notes,
  CalendarToday,
  Schedule,
  MedicalServices,
  HowToReg,
  Assignment,
  Download,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  PageShell,
  HelpButton,
  IdentityBanner,
  IdentifierChip,
  InfoItem,
  SectionCard,
  StatCard,
  RouteActionButton,
  ActionButton,
} from '../../components';
import { pode } from '../../authorization/pode';
import {
  type Appointment,
  AppointmentStatus,
  APPOINTMENT_STATUS_COLORS,
  APPOINTMENT_STATUS_LABELS,
  CONFIRMATION_LABELS,
  AppointmentConfirmation,
} from '../../types/appointment';
import appointmentService from '../../services/appointmentService';
import {
  APPOINTMENT_EXAM_STATUS_LABELS,
  APPOINTMENT_EXAM_STATUS_COLORS,
  APPOINTMENT_EXAM_RESULT_LABELS,
  APPOINTMENT_EXAM_RESULT_COLORS,
  ReferralStatus,
  type AppointmentExamResult,
} from '../../types/appointmentExam';
import type { AppointmentReferral } from '../../types/appointmentReferral';
import appointmentExamService from '../../services/appointmentExamService';
import { openSignedFile } from '../../services/fileAccess';
import { medicalRecordService } from '../../services/medicalRecordService';
import { mensagemDeFalha } from '../../utils/acessoNegado';

/* ─── helper: format weekday ─── */
const getWeekday = (dateStr: string): string => {
  try {
    const [y, m, d] = dateStr.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    return date.toLocaleDateString('pt-BR', { weekday: 'long' });
  } catch {
    return '';
  }
};

/* ─── helper: format attendance status ─── */
const formatAttendanceStatus = (status?: string): string => {
  if (!status) return '—';
  const map: Record<string, string> = {
    NOT_ATTENDED: 'Não Atendido',
    CHECKED_IN: 'Check-in Realizado',
    TRIAGED: 'Triagem Realizada',
    IN_PROGRESS: 'Em Atendimento',
    COMPLETED: 'Atendimento Concluído',
  };
  return map[status] || status;
};

/* ─── helper: format period ─── */
const formatPeriod = (period: string | null): string => {
  if (!period) return '—';
  return period === 'morning' ? 'Manhã' : 'Tarde';
};

/* ─── helper: build clinic address string ─── */
const buildClinicAddress = (appointment: Appointment): string | null => {
  const clinic = appointment.clinic;
  if (!clinic) return null;

  // Prefer providerAddress if available
  const addr = clinic.providerAddress || clinic;
  const line1 = [addr.address, addr.addressNumber].filter(Boolean).join(', ');
  const complement = clinic.providerAddress?.complement;
  const line2 = [addr.neighborhood, complement].filter(Boolean).join(' — ');
  const line3 = [addr.city, addr.state].filter(Boolean).join('/');

  return [line1, line2, line3].filter(Boolean).join(' — ');
};

/* ══════════════════════════════════════════════════════════════ */

export const AppointmentDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { id } = useParams<{ id: string }>();
  const { enqueueSnackbar } = useSnackbar();

  const [appointment, setAppointment] = useState<Appointment | null>(null);
  const [regeneratingGuide, setRegeneratingGuide] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (id) {
      loadAppointment(parseInt(id, 10));
    }
  }, [id]);

  const loadAppointment = async (appointmentId: number) => {
    try {
      setLoading(true);
      setError(null);
      const data = await appointmentService.getAppointmentById(appointmentId);
      setAppointment(data);
    } catch (err: unknown) {
      console.error('Error loading appointment:', err);
      setError('Erro ao carregar agendamento');
      enqueueSnackbar('Erro ao carregar agendamento', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    navigate(`/appointments/${id}/edit`);
  };

  const handleBack = () => {
    navigate('/appointments');
  };

  const handleConfirm = async () => {
    if (!appointment) return;
    try {
      setActionLoading(true);
      await appointmentService.confirmAppointment(appointment.id);
      enqueueSnackbar('Agendamento confirmado com sucesso', { variant: 'success' });
      loadAppointment(appointment.id);
    } catch (err: unknown) {
      const errorMessage = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Erro ao confirmar agendamento';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleComplete = async () => {
    if (!appointment) return;
    try {
      setActionLoading(true);
      await appointmentService.completeAppointment(appointment.id);
      enqueueSnackbar('Agendamento concluído com sucesso', { variant: 'success' });
      loadAppointment(appointment.id);
    } catch (err: unknown) {
      const errorMessage = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Erro ao concluir agendamento';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelClick = () => {
    setCancelDialogOpen(true);
  };

  // (Re)gera a Guia de Atendimento sob demanda — caso a geração automática na
  // criação do agendamento tenha falhado (ex.: Chrome indisponível). #270
  const handleRegenerateGuide = async () => {
    if (!appointment) return;
    setRegeneratingGuide(true);
    try {
      await medicalRecordService.generateGuidePdf(appointment.id);
      enqueueSnackbar('Guia gerada com sucesso', { variant: 'success' });
      await loadAppointment(appointment.id);
    } catch (err: unknown) {
      enqueueSnackbar(mensagemDeFalha(err, 'Falha ao gerar a Guia'), { variant: 'error' });
    } finally {
      setRegeneratingGuide(false);
    }
  };

  const handleCancelConfirm = async () => {
    if (!appointment) return;
    try {
      setActionLoading(true);
      await appointmentService.cancelAppointment(appointment.id);
      enqueueSnackbar('Agendamento cancelado com sucesso', { variant: 'success' });
      setCancelDialogOpen(false);
      loadAppointment(appointment.id);
    } catch (err: unknown) {
      const errorMessage = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Erro ao cancelar agendamento';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const baseBreadcrumbs = [
    { label: 'Início', path: '/' },
    { label: 'Agendamentos', path: '/appointments' },
    { label: `#${id}` },
  ];

  if (loading) {
    return (
      <PageShell breadcrumbs={baseBreadcrumbs} title="Agendamento">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  if (error || !appointment) {
    return (
      <PageShell breadcrumbs={baseBreadcrumbs} title="Agendamento">
        <Alert severity="error">{error || 'Agendamento não encontrado'}</Alert>
        <Button sx={{ mt: 2 }} variant="outlined" onClick={handleBack}>
          Voltar
        </Button>
      </PageShell>
    );
  }

  const canEdit = appointmentService.canEdit(appointment.status);
  const canCancel = appointmentService.canCancel(appointment.status);

  const statusColor = APPOINTMENT_STATUS_COLORS[appointment.status as AppointmentStatus] || 'default';
  const statusLabel = APPOINTMENT_STATUS_LABELS[appointment.status as AppointmentStatus] || appointment.status;

  const clinicAddress = buildClinicAddress(appointment);
  const hasExams = appointment.appointmentExams && appointment.appointmentExams.length > 0;
  const hasReferrals = appointment.referrals && appointment.referrals.length > 0;
  const hasAttendance = (appointment.attendanceStatus && appointment.attendanceStatus !== 'NOT_ATTENDED') || appointment.presenceConfirmedAt;
  const hasApproval = appointment.approvedAt || appointment.rejectedAt;
  const hasOutOfSlot = appointment.isOutOfSlot;
  const hasNotes = appointment.notice || appointment.observation || appointment.asoNotes;

  const timeDisplay = appointment.period
    ? formatPeriod(appointment.period)
    : (appointmentService.formatTime(appointment.hour, appointment.minute) || '—');

  const confirmationLabel = appointment.confirmation
    ? (CONFIRMATION_LABELS[appointment.confirmation as AppointmentConfirmation] || appointment.confirmation)
    : 'Não registrada';

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/' },
        { label: 'Agendamentos', path: '/appointments' },
        { label: `#${appointment.id}` },
      ]}
      actions={
        <>
          <HelpButton entity="appointments" screen="detalhes" size="small" />
          <Button variant="outlined" size="small" startIcon={<ArrowBack />} onClick={handleBack}>
            Voltar
          </Button>
          {appointment.status === AppointmentStatus.PENDENTE && (
            <ActionButton acao="appointment.update" rotulo="Confirmar" variant="contained" color="success" size="small" startIcon={<CheckCircle />} onClick={handleConfirm} disabled={actionLoading}>
              Confirmar
            </ActionButton>
          )}
          {appointment.status === AppointmentStatus.CONFIRMADO && (
            <ActionButton acao="appointment.update" rotulo="Concluir" variant="contained" color="success" size="small" startIcon={<Done />} onClick={handleComplete} disabled={actionLoading}>
              Concluir
            </ActionButton>
          )}
          {canEdit && (
            <RouteActionButton
              para="/appointments/:id/edit"
              exige={pode('appointment.update')}
              aoAgir={handleEdit}
              variant="outlined"
              size="small"
              startIcon={<Edit />}
              disabled={actionLoading}
            >
              Editar
            </RouteActionButton>
          )}
          {canCancel && (
            <ActionButton acao="appointment.delete" rotulo="Cancelar" variant="outlined" color="error" size="small" startIcon={<Cancel />} onClick={handleCancelClick} disabled={actionLoading}>
              Cancelar
            </ActionButton>
          )}
          {hasReferrals && (
            <RouteActionButton
              para="/appointments/:id/referral-reports"
              exige={pode('referral_report.manage')}
              aoAgir={() => navigate(`/appointments/${id}/referral-reports`)}
              variant="outlined"
              size="small"
              startIcon={<Assignment />}
            >
              Laudos Terceirizados
            </RouteActionButton>
          )}
        </>
      }
    >
        {/* ═══ IDENTIDADE ═══ */}
        <IdentityBanner
          avatar={<CalendarToday sx={{ fontSize: 32 }} />}
          name={`Agendamento #${appointment.id}`}
          status={
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip label={statusLabel} color={statusColor} size="small" />
              <Chip label={appointment.examType} variant="outlined" size="small" color="primary" />
            </Box>
          }
          context={
            <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
              <Person sx={{ fontSize: 16 }} />
              <Typography component="span" variant="body2" fontWeight={600}>
                {appointment.employee?.name || `Funcionário #${appointment.employeeId}`}
              </Typography>
              <Typography component="span" color="text.disabled">›</Typography>
              <span>{appointment.company?.tradeName || appointment.company?.legalName || '—'}</span>
              {appointment.employee?.department?.name && (
                <>
                  <Typography component="span" color="text.disabled">›</Typography>
                  <span>{appointment.employee.department.name}</span>
                </>
              )}
              {appointment.employee?.occupation?.name && (
                <>
                  <Typography component="span" color="text.disabled">›</Typography>
                  <Typography component="span" variant="body2" fontWeight={600}>
                    {appointment.employee.occupation.name}
                  </Typography>
                </>
              )}
            </Box>
          }
          identifiers={
            appointment.employee?.cpf ? (
              <IdentifierChip label={appointment.employee.cpf} />
            ) : undefined
          }
        />

        {/* ═══ STAT CARDS ═══ */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 6, sm: 3 }}>
            <StatCard
              icon={<CalendarToday fontSize="small" />}
              label="Data"
              value={appointmentService.formatDate(appointment.selectedDate)}
              sublabel={getWeekday(appointment.selectedDate)}
              color={theme.palette.info.main}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <StatCard
              icon={<Schedule fontSize="small" />}
              label={appointment.period ? 'Período' : 'Horário'}
              value={timeDisplay}
              color={theme.palette.secondary.main}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <StatCard
              icon={<MedicalServices fontSize="small" />}
              label="Tipo de Exame"
              value={appointment.examType}
              color={theme.palette.primary.main}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <StatCard
              icon={<HowToReg fontSize="small" />}
              label="Presença"
              value={confirmationLabel}
              color={
                appointment.confirmation === AppointmentConfirmation.PRESENTE
                  ? theme.palette.success.main
                  : appointment.confirmation === AppointmentConfirmation.AUSENTE
                  ? theme.palette.error.main
                  : theme.palette.warning.main
              }
            />
          </Grid>
        </Grid>

        {/* ═══ CANCELLATION REASON ALERT ═══ */}
        {appointment.status === AppointmentStatus.CANCELADO && appointment.cancellationReason && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <strong>Motivo do cancelamento:</strong> {appointment.cancellationReason}
          </Alert>
        )}

        {/* ═══ CLINIC APPROVAL STATUS ═══ */}
        {appointment.status === AppointmentStatus.AGUARDANDO_APROVACAO && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <strong>Aguardando aprovação das clínicas</strong>
            <Box sx={{ mt: 1 }}>
              {/* Main clinic */}
              <Box display="flex" alignItems="center" gap={0.5}>
                {appointment.approvedAt ? (
                  <Chip label="Aceito" color="success" size="small" variant="outlined" />
                ) : (
                  <Chip label="Aguardando" color="warning" size="small" variant="outlined" />
                )}
                <Typography variant="body2">
                  {appointment.clinic?.tradeName || appointment.clinic?.legalName || 'Clínica principal'}
                </Typography>
              </Box>
              {/* Referral clinics */}
              {appointment.referrals?.map((referral: AppointmentReferral) => (
                <Box key={referral.id} display="flex" alignItems="center" gap={0.5} mt={0.5}>
                  {referral.status === ReferralStatus.ACCEPTED ? (
                    <Chip label="Aceito" color="success" size="small" variant="outlined" />
                  ) : referral.status === ReferralStatus.RESCHEDULE_REQUESTED ? (
                    <Chip label="Reagendamento" color="warning" size="small" variant="outlined" />
                  ) : referral.status === ReferralStatus.REFUSED ? (
                    <Chip label="Recusado" color="error" size="small" variant="outlined" />
                  ) : (
                    <Chip label="Aguardando" color="warning" size="small" variant="outlined" />
                  )}
                  <Typography variant="body2">
                    {referral.clinic?.tradeName || referral.clinic?.legalName || `Clínica #${referral.clinicId}`}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Alert>
        )}

        {/* ═══ REAGENDAR ALERT ═══ */}
        {appointment.status === AppointmentStatus.REAGENDAR && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <strong>Reagendamento solicitado</strong> — Uma clínica solicitou reagendamento. Edite o agendamento para definir nova data/horário.
          </Alert>
        )}

        {/* ═══ SECTION CARDS ═══ */}
        <Grid container spacing={2.5} sx={{ mb: 3 }}>

          {/* 1. Funcionário */}
          <Grid size={{ xs: 12, md: 6 }}>
            <SectionCard
              title="Funcionário"
              icon={<Person fontSize="small" />}
              accentColor={theme.palette.primary.main}
            >
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <InfoItem label="Nome" value={appointment.employee?.name || `ID: ${appointment.employeeId}`} />
                </Grid>
                {appointment.employee?.cpf && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <InfoItem label="CPF" value={appointment.employee.cpf} />
                  </Grid>
                )}
                {appointment.employee?.department?.name && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <InfoItem label="Departamento" value={appointment.employee.department.name} />
                  </Grid>
                )}
                {appointment.employee?.occupation?.name && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <InfoItem label="Cargo" value={appointment.employee.occupation.name} />
                  </Grid>
                )}
                {appointment.responsibleEmail && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <InfoItem label="E-mail Responsável" value={appointment.responsibleEmail} />
                  </Grid>
                )}
                {appointment.responsiblePhone && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <InfoItem label="Telefone Responsável" value={appointment.responsiblePhone} />
                  </Grid>
                )}
              </Grid>
            </SectionCard>
          </Grid>

          {/* 2. Documentos — sempre visível, botões desabilitados se PDF indisponível */}
          <Grid size={{ xs: 12, md: 6 }}>
            <SectionCard
              title="Documentos"
              icon={<Description fontSize="small" />}
              accentColor="#FF8F00"
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Description />}
                  disabled={!appointment.pdfPath}
                  onClick={async () => {
                    if (!appointment.pdfPath) return;
                    const target = window.open('about:blank', '_blank');
                    if (!target) return;
                    try {
                      await openSignedFile(appointment.pdfPath, target);
                    } catch (err) {
                      target.close();
                      enqueueSnackbar(err instanceof Error ? err.message : 'Erro ao abrir ASO', { variant: 'error' });
                    }
                  }}
                  sx={{ justifyContent: 'flex-start' }}
                >
                  Visualizar ASO
                </Button>
                {!appointment.pdfPath && (
                  <Typography variant="caption" color="text.secondary" sx={{ pl: 0.5, mt: -0.5 }}>
                    Disponível após a conclusão do atendimento.
                  </Typography>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Description />}
                  disabled={!appointment.guidePdfPath}
                  onClick={async () => {
                    if (!appointment.guidePdfPath) return;
                    const target = window.open('about:blank', '_blank');
                    if (!target) return;
                    try {
                      await openSignedFile(appointment.guidePdfPath, target);
                    } catch (err) {
                      target.close();
                      enqueueSnackbar(err instanceof Error ? err.message : 'Erro ao abrir Guia', { variant: 'error' });
                    }
                  }}
                  sx={{ justifyContent: 'flex-start' }}
                >
                  Visualizar Guia
                </Button>
                {!appointment.guidePdfPath && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pl: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Guia ainda não gerada.
                    </Typography>
                    <ActionButton
                      acao="medical-records.generate-guide"
                      rotulo="Gerar guia agora"
                      variant="text"
                      size="small"
                      disabled={regeneratingGuide}
                      startIcon={regeneratingGuide ? <CircularProgress size={14} /> : <Description />}
                      onClick={handleRegenerateGuide}
                      sx={{ justifyContent: 'flex-start', alignSelf: 'flex-start' }}
                    >
                      {regeneratingGuide ? 'Gerando Guia…' : 'Gerar Guia novamente'}
                    </ActionButton>
                  </Box>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Description />}
                  disabled={!appointment.medicalRecordPdfPath}
                  onClick={async () => {
                    if (!appointment.medicalRecordPdfPath) return;
                    const target = window.open('about:blank', '_blank');
                    if (!target) return;
                    try {
                      await openSignedFile(appointment.medicalRecordPdfPath, target);
                    } catch (err) {
                      target.close();
                      enqueueSnackbar(err instanceof Error ? err.message : 'Erro ao abrir Prontuário', { variant: 'error' });
                    }
                  }}
                  sx={{ justifyContent: 'flex-start' }}
                >
                  Visualizar Prontuário
                </Button>
                {!appointment.medicalRecordPdfPath && (
                  <Typography variant="caption" color="text.secondary" sx={{ pl: 0.5, mt: -0.5 }}>
                    Disponível após a conclusão do atendimento.
                  </Typography>
                )}
                {/* Guias de Encaminhamento */}
                {appointment.referrals?.map((referral) =>
                  referral.guidePdfPath && (
                    <Button
                      key={referral.id}
                      variant="outlined"
                      size="small"
                      startIcon={<Download />}
                      onClick={async () => {
                        if (!referral.guidePdfPath) return;
                        const target = window.open('about:blank', '_blank');
                        if (!target) return;
                        try {
                          await openSignedFile(referral.guidePdfPath, target);
                        } catch (err) {
                          target.close();
                          enqueueSnackbar(err instanceof Error ? err.message : 'Erro ao abrir Guia', { variant: 'error' });
                        }
                      }}
                      sx={{ justifyContent: 'flex-start' }}
                    >
                      Guia — {referral.clinic?.tradeName || referral.clinic?.legalName || 'Clínica'}
                    </Button>
                  )
                )}
              </Box>
              {/* Ações de encaminhamento */}
              {hasReferrals && (
                <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                  {appointment.referrals?.some(r => r.status === ReferralStatus.PENDING_SEND || r.status === ReferralStatus.SENT) && (
                    <ActionButton
                      acao="appointment.update"
                      rotulo="Reenviar encaminhamentos"
                      size="small"
                      variant="outlined"
                      startIcon={<Send />}
                      onClick={async () => {
                        try {
                          await appointmentExamService.sendReferrals(appointment.id);
                          enqueueSnackbar('Encaminhamentos enviados com sucesso', { variant: 'success' });
                          loadAppointment(appointment.id);
                        } catch {
                          enqueueSnackbar('Erro ao enviar encaminhamentos', { variant: 'error' });
                        }
                      }}
                    >
                      Reenviar
                    </ActionButton>
                  )}
                  <RouteActionButton
                    para="/appointments/:id/referral-reports"
                    exige={pode('referral_report.manage')}
                    aoAgir={() => navigate(`/appointments/${id}/referral-reports`)}
                    size="small"
                    variant="text"
                    startIcon={<Assignment />}
                  >
                    Ver Laudos
                  </RouteActionButton>
                </Box>
              )}
            </SectionCard>
          </Grid>

          {/* 3. Local do Atendimento */}
          <Grid size={{ xs: 12, md: 6 }}>
            <SectionCard
              title="Local do Atendimento"
              icon={<LocationOn fontSize="small" />}
              accentColor={theme.palette.success.main}
            >
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <InfoItem
                    label="Clínica"
                    value={appointment.clinic?.tradeName || appointment.clinic?.legalName || '—'}
                  />
                </Grid>
                {appointment.doctor?.name && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <InfoItem label="Médico" value={appointment.doctor.name} />
                  </Grid>
                )}
                {clinicAddress && (
                  <Grid size={12}>
                    <Divider sx={{ my: 1 }} />
                    <InfoItem
                      icon={<LocationOn sx={{ fontSize: 14 }} />}
                      label="Endereço"
                      value={clinicAddress}
                      fullWidth
                    />
                  </Grid>
                )}
                {appointment.scheduleSlot && (
                  <>
                    <Grid size={12}>
                      <Divider sx={{ my: 1 }} />
                    </Grid>
                    {appointment.scheduleSlot.location && (
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <InfoItem label="Local do Slot" value={appointment.scheduleSlot.location} />
                      </Grid>
                    )}
                    {appointment.scheduleSlot.startTime && (
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <InfoItem
                          label="Horário do Slot"
                          value={`${appointment.scheduleSlot.startTime}${appointment.scheduleSlot.endTime ? ` — ${appointment.scheduleSlot.endTime}` : ''}`}
                        />
                      </Grid>
                    )}
                    {appointment.scheduleSlot.doctorName && (
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <InfoItem label="Médico do Slot" value={appointment.scheduleSlot.doctorName} />
                      </Grid>
                    )}
                  </>
                )}
              </Grid>
            </SectionCard>
          </Grid>

          {/* 3. Exames Complementares */}
          {hasExams && (
            <Grid size={{ xs: 12, md: 6 }}>
              <SectionCard
                title="Exames Complementares"
                icon={<Science fontSize="small" />}
                accentColor={theme.palette.info.main}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {appointment.appointmentExams!.map((exam) => (
                    <Box key={exam.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="body2" fontWeight={600} sx={{ minWidth: 150 }}>
                        {exam.occupationalExam?.examName ?? `Exame #${exam.occupationalExamId}`}
                      </Typography>
                      <Chip
                        label={APPOINTMENT_EXAM_STATUS_LABELS[exam.status] ?? exam.status}
                        color={APPOINTMENT_EXAM_STATUS_COLORS[exam.status] ?? 'default'}
                        size="small"
                        variant="outlined"
                      />
                      {exam.result && (
                        <Chip
                          label={APPOINTMENT_EXAM_RESULT_LABELS[exam.result as AppointmentExamResult] ?? exam.result}
                          color={APPOINTMENT_EXAM_RESULT_COLORS[exam.result as AppointmentExamResult] ?? 'default'}
                          size="small"
                        />
                      )}
                      {exam.referralId && (
                        <Chip
                          label={`→ ${exam.referral?.clinic?.tradeName || exam.referral?.clinic?.legalName || 'Encaminhado'}`}
                          color="secondary"
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  ))}
                </Box>
              </SectionCard>
            </Grid>
          )}

          {/* 6. Atendimento */}
          {hasAttendance && (
            <Grid size={{ xs: 12, md: 6 }}>
              <SectionCard
                title="Atendimento"
                icon={<AccessTime fontSize="small" />}
                accentColor={theme.palette.info.main}
              >
                <Grid container spacing={2}>
                  {appointment.attendanceStatus && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <InfoItem label="Status" value={formatAttendanceStatus(appointment.attendanceStatus)} />
                    </Grid>
                  )}
                  {appointment.attendanceType && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <InfoItem label="Tipo" value={appointment.attendanceType} />
                    </Grid>
                  )}
                  {appointment.presenceConfirmedAt && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <InfoItem
                        label="Presença Confirmada"
                        value={new Date(appointment.presenceConfirmedAt).toLocaleString('pt-BR')}
                      />
                    </Grid>
                  )}
                  {appointment.attendanceStartedAt && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <InfoItem
                        label="Início"
                        value={new Date(appointment.attendanceStartedAt).toLocaleString('pt-BR')}
                      />
                    </Grid>
                  )}
                  {appointment.attendanceCompletedAt && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <InfoItem
                        label="Conclusão"
                        value={new Date(appointment.attendanceCompletedAt).toLocaleString('pt-BR')}
                      />
                    </Grid>
                  )}
                  {appointment.queueNumber != null && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <InfoItem label="Número da Fila" value={String(appointment.queueNumber)} />
                    </Grid>
                  )}
                  {appointment.receptionist?.name && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <InfoItem label="Recepcionista" value={appointment.receptionist.name} />
                    </Grid>
                  )}
                </Grid>
              </SectionCard>
            </Grid>
          )}

          {/* 7. Aprovação */}
          {hasApproval && (
            <Grid size={{ xs: 12, md: 6 }}>
              <SectionCard
                title="Aprovação"
                icon={<CheckCircle fontSize="small" />}
                accentColor={theme.palette.warning.main}
              >
                <Grid container spacing={2}>
                  {appointment.approvedAt && (
                    <>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <InfoItem label="Aprovado por" value={appointment.approvedBy?.name || '—'} />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <InfoItem label="Data" value={new Date(appointment.approvedAt).toLocaleString('pt-BR')} />
                      </Grid>
                    </>
                  )}
                  {appointment.rejectedAt && (
                    <>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <InfoItem label="Rejeitado por" value={appointment.rejectedBy?.name || '—'} />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <InfoItem label="Data" value={new Date(appointment.rejectedAt).toLocaleString('pt-BR')} />
                      </Grid>
                      {appointment.rejectionReason && (
                        <Grid size={12}>
                          <InfoItem label="Motivo da Rejeição" value={appointment.rejectionReason} fullWidth />
                        </Grid>
                      )}
                    </>
                  )}
                </Grid>
              </SectionCard>
            </Grid>
          )}

          {/* 8. Fora de Slot */}
          {hasOutOfSlot && (
            <Grid size={{ xs: 12, md: 6 }}>
              <SectionCard
                title="Fora de Slot"
                icon={<EventBusy fontSize="small" />}
                accentColor={theme.palette.error.main}
              >
                <Grid container spacing={2}>
                  {appointment.outOfSlotBookedBy?.name && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <InfoItem label="Agendado por" value={appointment.outOfSlotBookedBy.name} />
                    </Grid>
                  )}
                  {appointment.outOfSlotBookedAt && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <InfoItem label="Data" value={new Date(appointment.outOfSlotBookedAt).toLocaleString('pt-BR')} />
                    </Grid>
                  )}
                  {appointment.outOfSlotReason && (
                    <Grid size={12}>
                      <InfoItem label="Motivo" value={appointment.outOfSlotReason} fullWidth />
                    </Grid>
                  )}
                </Grid>
              </SectionCard>
            </Grid>
          )}

          {/* 9. Observações */}
          {hasNotes && (
            <Grid size={{ xs: 12, md: 6 }}>
              <SectionCard
                title="Observações"
                icon={<Notes fontSize="small" />}
                accentColor="#546E7A"
              >
                <Grid container spacing={2}>
                  {appointment.notice && (
                    <Grid size={12}>
                      <InfoItem label="Aviso" value={appointment.notice} fullWidth />
                    </Grid>
                  )}
                  {appointment.observation && (
                    <Grid size={12}>
                      <InfoItem label="Observação" value={appointment.observation} fullWidth />
                    </Grid>
                  )}
                  {appointment.asoNotes && (
                    <Grid size={12}>
                      <InfoItem label="Notas ASO" value={appointment.asoNotes} fullWidth />
                    </Grid>
                  )}
                </Grid>
              </SectionCard>
            </Grid>
          )}
        </Grid>

        {/* ═══ FOOTER TIMESTAMPS ═══ */}
        <Box sx={{ mt: 1, mb: 2 }}>
          <Typography variant="caption" color="text.disabled" display="block">
            Criado em {new Date(appointment.createdAt).toLocaleString('pt-BR')}
          </Typography>
          <Typography variant="caption" color="text.disabled" display="block">
            Atualizado em {new Date(appointment.updatedAt).toLocaleString('pt-BR')}
          </Typography>
        </Box>

        {/* Cancel Confirmation Dialog */}
        <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
          <DialogTitle>Confirmar Cancelamento</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Tem certeza que deseja cancelar este agendamento?
              <br />
              Esta ação não pode ser desfeita.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCancelDialogOpen(false)}>Voltar</Button>
            <Button
              onClick={handleCancelConfirm}
              color="error"
              variant="contained"
              disabled={actionLoading}
            >
              {actionLoading ? <CircularProgress size={24} /> : 'Cancelar Agendamento'}
            </Button>
          </DialogActions>
        </Dialog>
    </PageShell>
  );
};

export default AppointmentDetailsPage;
